/**
 * CartDrilldown — full document grid for one cart (in-flight account opening).
 *
 * Routed via `/rdl/cart/:cartId`. Cross-link to RAO so the FSR can resume
 * the cart's wizard if work is paused.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, ExternalLink, Plus } from '@retail-ops/panel-shell/fi';
import type { DocumentRef } from '@retail-ops/api-client';
import { Button, Banner } from '@/components/primitives';
import { DocumentTile } from '../components/DocumentTile';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { DocumentUploadModal } from '../components/DocumentUploadModal';
import { useDocumentsByCart } from '../hooks/useDocumentsByCart';
import { customerLabelFromCif } from '../components/humanize';
import { useAuth } from '@/contexts/AuthContext';

export function CartDrilldown(): JSX.Element {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = cartId ?? '';
  const { documents, loading, error, isFixture, fixtureNotice, refresh } = useDocumentsByCart(cart);
  const [preview, setPreview] = useState<DocumentRef | null>(null);
  const [uploadOpen, setUploadOpen] = useState<boolean>(false);

  const cifId = documents[0]?.cifId ?? '';

  const canDelete = !!user && (
    user.primaryRole === 'Manager' ||
    user.primaryRole === 'Executive' ||
    user.primaryRole === 'Admin' ||
    user.primaryRole === 'SuperAdmin'
  );

  return (
    <div className="rdl-shell">
      <Button
        variant="ghost"
        size="sm"
        startIcon={<ChevronLeft size={14} />}
        onClick={() => navigate('/rdl')}
      >
        Back to documents
      </Button>

      <header className="rdl-shell__header">
        <div>
          <h1 className="rdl-shell__title">
            Cart for {cifId ? customerLabelFromCif(cifId) : 'this customer'}
          </h1>
          <p className="rdl-shell__sub">
            Documents attached to this in-flight account-opening cart. Anything captured during the
            wizard lands here automatically.
          </p>
        </div>
        <div className="rdl-shell__actions">
          <Button
            variant="ghost"
            size="sm"
            startIcon={<ExternalLink size={14} />}
            onClick={() => navigate(`/rao?cartId=${encodeURIComponent(cart)}`)}
          >
            Resume in New Acct
          </Button>
          <Button
            variant="primary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload document
          </Button>
        </div>
      </header>

      {error && <Banner tone="err" title="Documents could not be loaded">{error}</Banner>}
      {isFixture && fixtureNotice && <Banner tone="info" title="Demo data">{fixtureNotice}</Banner>}

      {loading && (
        <div style={{ padding: 'var(--sp-9)', textAlign: 'center', color: 'var(--ink-500)' }}>
          Loading cart documents…
        </div>
      )}

      {!loading && documents.length === 0 && (
        <div className="rdl-empty">
          <h3 className="rdl-empty__title">No documents attached to this cart yet</h3>
          <p className="rdl-empty__msg">
            Scan an ID or upload a proof document to get started. The wizard's required-document
            checklist will tick green once each slot is filled.
          </p>
          <Button
            variant="primary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload first document
          </Button>
        </div>
      )}

      {documents.length > 0 && (
        <div className="rdl-grid" role="list" aria-label={`Documents for cart ${cart}`}>
          {documents.map((d) => (
            <div role="listitem" key={d.documentId}>
              <DocumentTile doc={d} onPreview={setPreview} showCart={false} />
            </div>
          ))}
        </div>
      )}

      <DocumentPreviewModal
        open={!!preview}
        doc={preview}
        onClose={() => setPreview(null)}
        canDelete={canDelete}
      />
      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultCifId={cifId}
        defaultCartId={cart}
        onUploaded={() => {
          refresh();
        }}
      />
    </div>
  );
}
